// Network-aware utility functions
export interface NetworkInfo {
  effectiveType?: string;
  saveData?: boolean;
  downlink?: number;
  online: boolean;
}

/**
 * Gets current network information safely (SSR-compatible)
 */
export function getNetworkInfo(): NetworkInfo {
  if (typeof window === 'undefined') {
    return { online: true };
  }
  
  const conn = (navigator as any).connection;
  return {
    online: navigator.onLine,
    effectiveType: conn?.effectiveType,
    saveData: conn?.saveData,
    downlink: conn?.downlink
  };
}

/**
 * Determines if current network conditions suggest lite mode should be enabled
 * Based on Data Saver or slow connection types (2G, slow-2G, 3G)
 */
export function shouldAutoEnableLiteMode(): boolean {
  if (typeof window === 'undefined') return false;
  
  const network = getNetworkInfo();
  const { effectiveType, saveData } = network;
  
  return saveData || 
         effectiveType === '2g' || 
         effectiveType === 'slow-2g' || 
         effectiveType === '3g';
}

/**
 * Gets a human-readable description of current network conditions
 */
export function getNetworkDescription(): string {
  const network = getNetworkInfo();
  
  if (!network.online) return 'Offline';
  
  const { effectiveType, saveData, downlink } = network;
  
  if (saveData) return 'Data Saver Enabled';
  
  const speedInfo = downlink ? ` (${downlink.toFixed(1)} Mbps)` : '';
  
  switch (effectiveType) {
    case 'slow-2g': return `Very Slow Connection${speedInfo}`;
    case '2g': return `Slow Connection${speedInfo}`;
    case '3g': return `Moderate Connection${speedInfo}`;
    case '4g': return `Fast Connection${speedInfo}`;
    default: return `Unknown Connection${speedInfo}`;
  }
}